/**
 * SectorContext — global state for the current user's locked sector.
 *
 * Covers BOTH roles:
 *   • employer      → organizations.sector  (GET /api/organizations/me)
 *   • professional  → profiles.sector       (GET /api/profiles/me)
 *
 * Consumers:
 *   1. SectorGate — auto-renders the blocking modal when the user is
 *      logged in AND sector is null/missing.
 *   2. SectorStatusBanner (EmployerDashboard) — manual re-trigger CTA.
 *
 * Defensive: handles null, undefined, '', and missing-key sector values
 * identically (all treated as "missing").
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const SectorContext = createContext(null);

const normalizeSector = (raw) => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'string') return null;
  if (raw.trim() === '') return null;
  return raw;
};

export const SectorProvider = ({ children }) => {
  const { user, loading } = useAuth();
  const role = user?.role;
  const isGated = role === 'employer' || role === 'professional';
  const [sector, setSector] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading || !isGated) return;
    const token = localStorage.getItem('access_token');
    const endpoint = role === 'employer' ? '/api/organizations/me' : '/api/profiles/me';
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const s = normalizeSector(res?.data?.sector);
        setSector(s);
        setHasFetched(true);
        setError(null);
        if (!s) setModalOpen(true);
      })
      .catch((e) => {
        // Never hard-block on transient failures (401/5xx) — the modal only
        // opens from a SUCCESSFUL fetch that confirms sector is missing.
        setSector(null);
        setHasFetched(true);
        setError(e?.response?.status >= 500 ? 'Kunne ikke laste sektor-info' : null);
      });
  }, [isGated, role, loading]);

  const requireSetup = useCallback(() => {
    setModalOpen(true);
  }, []);

  const markResolved = useCallback((newSector) => {
    setSector(normalizeSector(newSector));
    setModalOpen(false);
  }, []);

  // Derived: non-gated roles never block, never need check.
  const hasChecked = isGated ? hasFetched : true;
  const effectiveSector = isGated ? sector : null;
  const effectiveModal = isGated && modalOpen;

  const value = {
    sector: effectiveSector,
    role: isGated ? role : null,
    isEmployerMissingSector: role === 'employer' && hasFetched && !effectiveSector,
    isLocked: Boolean(effectiveSector),
    hasChecked,
    modalOpen: effectiveModal,
    error: isGated ? error : null,
    requireSetup,
    markResolved,
  };

  return <SectorContext.Provider value={value}>{children}</SectorContext.Provider>;
};

export const useSector = () => {
  const ctx = useContext(SectorContext);
  if (!ctx) {
    throw new Error('useSector must be used inside <SectorProvider>');
  }
  return ctx;
};
